import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchUserSignaturesRequest,
  fetchUserSignaturesSuccess,
  fetchUserSignaturesFailure,
  createUserSignatureRequest,
  createUserSignatureSuccess,
  createUserSignatureFailure,
  updateUserSignatureRequest,
  updateUserSignatureSuccess,
  updateUserSignatureFailure,
  deleteUserSignatureRequest,
  deleteUserSignatureSuccess,
  deleteUserSignatureFailure,
  setDefaultUserSignatureRequest,
  setDefaultUserSignatureSuccess,
  setDefaultUserSignatureFailure,
  setSelectedSignatureId,
  clearSignatureErrors,
} from '../store/actions/signatures.store.actions';
import {
  getUserSignatures,
  getUserSignaturesLoading,
  getUserSignaturesError,
  getDefaultUserSignature,
  getActiveUserSignatures,
  getSelectedSignatureId,
  getSignatureOperationLoading,
  getSignatureError,
} from '../store/selectors/signatures.selectors';
import {
  signaturesApi,
  type CreateUserSignatureRequest,
  type UpdateUserSignatureRequest,
} from '../api/signatures.api';
import type { UserSignature } from '../store/reducers/signatures.store';
import useNotifications from '../_lib/notifications/useNotifications';
import { useActor } from '../components/hooks/useActor';

export const useSignatures = () => {
  const dispatch = useDispatch();
  const notifications = useNotifications();

  // Every /v1/signatures endpoint is @PreAuthorize("hasAnyRole(@COWBELL_ACCOUNT)"),
  // so a mount from any other persona can only ever produce a 403 and a bare
  // "Forbidden" toast. Skip the request entirely for them (FIR-21141).
  const { isCowbell } = useActor();

  // Selectors
  const signatures = useSelector(getUserSignatures);
  const loading = useSelector(getUserSignaturesLoading);
  const error = useSelector(getUserSignaturesError);
  const defaultSignature = useSelector(getDefaultUserSignature);
  const activeSignatures = useSelector(getActiveUserSignatures);
  const selectedSignatureId = useSelector(getSelectedSignatureId);
  const operationLoading = useSelector(getSignatureOperationLoading);
  const operationError = useSelector(getSignatureError);

  // Fetch user signatures
  const fetchSignatures = useCallback(async () => {
    try {
      dispatch(fetchUserSignaturesRequest());
      const response = await signaturesApi.getUserSignatures();

      // Handle paginated response - extract signatures from content array
      let signaturesData: any[] = [];
      if (response.data && typeof response.data === 'object') {
        if (Array.isArray(response.data)) {
          // Direct array response
          signaturesData = response.data;
        } else if (
          (response.data as any).content &&
          Array.isArray((response.data as any).content)
        ) {
          // Paginated response with content array
          signaturesData = (response.data as any).content;
        }
      }

      dispatch(fetchUserSignaturesSuccess(signaturesData));
    } catch (err: unknown) {
      console.error('Fetch user signatures error:', err);
      const errorMessage =
        (err as any).response?.data?.error ||
        (err as any).message ||
        'Failed to fetch signatures';
      dispatch(fetchUserSignaturesFailure(errorMessage));
      notifications.error(errorMessage);
    }
  }, [dispatch, notifications]);

  // Create signature
  const createSignature = useCallback(
    async (signatureData: CreateUserSignatureRequest) => {
      try {
        dispatch(createUserSignatureRequest());
        const response = await signaturesApi.createUserSignature({
          data: signatureData,
        });
        // Ensure the response data is valid
        if (response.data && typeof response.data === 'object') {
          dispatch(createUserSignatureSuccess(response.data));
          notifications.success('Signature created successfully');
          return response.data;
        } else {
          throw new Error('Invalid response data from server');
        }
      } catch (err: unknown) {
        const errorMessage =
          (err as any).response?.data?.error ||
          (err as any).message ||
          'Failed to create signature';
        dispatch(createUserSignatureFailure(errorMessage));
        notifications.error(errorMessage);
        throw err;
      }
    },
    [dispatch, notifications]
  );

  // Update signature
  const updateSignature = useCallback(
    async (id: string, signatureData: UpdateUserSignatureRequest) => {
      try {
        dispatch(updateUserSignatureRequest());
        const response = await signaturesApi.updateUserSignature(id, {
          data: signatureData,
        });
        // Ensure the response data is valid
        if (response.data && typeof response.data === 'object') {
          dispatch(updateUserSignatureSuccess(response.data));
          notifications.success('Signature updated successfully');
          return response.data;
        } else {
          throw new Error('Invalid response data from server');
        }
      } catch (err: unknown) {
        const errorMessage =
          (err as any).response?.data?.error ||
          (err as any).message ||
          'Failed to update signature';
        dispatch(updateUserSignatureFailure(errorMessage));
        notifications.error(errorMessage);
        throw err;
      }
    },
    [dispatch, notifications]
  );

  // Delete signature
  const deleteSignature = useCallback(
    async (id: string) => {
      try {
        dispatch(deleteUserSignatureRequest());
        const response = await signaturesApi.deleteUserSignature(id);
        dispatch(deleteUserSignatureSuccess(id));
        notifications.success('Signature deleted successfully');
      } catch (err: unknown) {
        console.error('useSignatures: deleteSignature failed:', err);
        const errorMessage =
          (err as any).response?.data?.error ||
          (err as any).message ||
          'Failed to delete signature';
        dispatch(deleteUserSignatureFailure(errorMessage));
        notifications.error(errorMessage);
        throw err;
      }
    },
    [dispatch, notifications]
  );

  // Set default signature
  const setDefaultSignature = useCallback(
    async (id: string) => {
      try {
        dispatch(setDefaultUserSignatureRequest());
        await signaturesApi.setDefaultUserSignature(id);
        dispatch(setDefaultUserSignatureSuccess(id));
        notifications.success('Default signature updated');
      } catch (err: unknown) {
        const errorMessage =
          (err as any).response?.data?.error ||
          (err as any).message ||
          'Failed to set default signature';
        dispatch(setDefaultUserSignatureFailure(errorMessage));
        notifications.error(errorMessage);
        throw err;
      }
    },
    [dispatch, notifications]
  );

  // Set selected signature
  const setSelectedSignature = useCallback(
    (id: string | null) => {
      dispatch(setSelectedSignatureId(id));
    },
    [dispatch]
  );

  // Clear errors
  const clearErrors = useCallback(() => {
    dispatch(clearSignatureErrors());
  }, [dispatch]);

  // Find signature by ID
  const findSignatureById = useCallback(
    (id: string): UserSignature | undefined => {
      return Array.isArray(signatures)
        ? signatures.find((sig) => sig.id === id)
        : undefined;
    },
    [signatures]
  );

  // Get signature content by ID
  const getSignatureContent = useCallback(
    (id: string): string => {
      const signature = findSignatureById(id);
      return signature?.content || '';
    },
    [findSignatureById]
  );

  // Auto-fetch signatures on mount
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (
      isCowbell &&
      !hasInitialized.current &&
      (!Array.isArray(signatures) || signatures.length === 0) &&
      !loading &&
      !error
    ) {
      hasInitialized.current = true;
      fetchSignatures();
    }
  }, [signatures, loading, error, isCowbell]);

  return {
    // State
    signatures: Array.isArray(signatures) ? signatures : [],
    loading,
    error,
    defaultSignature,
    activeSignatures,
    selectedSignatureId,
    operationLoading,
    operationError,

    // Actions
    fetchSignatures,
    createSignature,
    updateSignature,
    deleteSignature,
    setDefaultSignature,
    setSelectedSignature,
    clearErrors,

    // Utilities
    findSignatureById,
    getSignatureContent,
  };
};
